// Factory presets for the built-in effects — curated starting points keyed by
// effect kind. Each preset lists the full param set for its kind (so applying a
// preset fully defines the sound). User-saved presets live in project.fxPresets.
import { EffectKind, EffectParam } from "../state/model";

export interface FxPresetDef { name: string; params: EffectParam; }

export const FX_PRESETS: Record<Exclude<EffectKind, "wam">, FxPresetDef[]> = {
  eq: [
    { name: "Air", params: { low: 0, mid: 0, high: 4, midFreq: 3000 } },
    { name: "Warmth", params: { low: 3, mid: -1, high: -1, midFreq: 500 } },
    { name: "Presence", params: { low: 0, mid: 3, high: 1, midFreq: 3000 } },
    { name: "Telephone", params: { low: -12, mid: 4, high: -10, midFreq: 1500 } },
    { name: "Low-cut Lite", params: { low: -6, mid: 0, high: 1, midFreq: 1000 } },
  ],
  compressor: [
    { name: "Vocal", params: { threshold: -20, ratio: 3, attack: 0.005, release: 0.15, knee: 20 } },
    { name: "Drum Punch", params: { threshold: -18, ratio: 4, attack: 0.02, release: 0.12, knee: 6 } },
    { name: "Bus Glue", params: { threshold: -16, ratio: 2, attack: 0.03, release: 0.3, knee: 30 } },
    { name: "Limiter-style", params: { threshold: -10, ratio: 20, attack: 0.001, release: 0.05, knee: 0 } },
    { name: "Gentle Leveler", params: { threshold: -24, ratio: 1.5, attack: 0.01, release: 0.4, knee: 30 } },
  ],
  delay: [
    { name: "Slapback", params: { time: 0.09, feedback: 0.1, mix: 0.28 } },
    { name: "1/8 (120 BPM)", params: { time: 0.25, feedback: 0.3, mix: 0.25 } },
    { name: "Dotted 1/8", params: { time: 0.375, feedback: 0.35, mix: 0.28 } },
    { name: "Dub Echo", params: { time: 0.4, feedback: 0.6, mix: 0.4 } },
    { name: "Long Throw", params: { time: 0.6, feedback: 0.45, mix: 0.3 } },
  ],
  reverb: [
    { name: "Small Room", params: { decay: 0.6, mix: 0.18 } },
    { name: "Vocal Plate", params: { decay: 1.6, mix: 0.22 } },
    { name: "Hall", params: { decay: 3.2, mix: 0.3 } },
    { name: "Cathedral", params: { decay: 5.5, mix: 0.35 } },
    { name: "Ambience", params: { decay: 0.9, mix: 0.12 } },
  ],
  gain: [
    { name: "+6 dB", params: { gain: 6 } },
    { name: "Unity", params: { gain: 0 } },
    { name: "−6 dB", params: { gain: -6 } },
  ],
  lowpass: [
    { name: "Warm", params: { cutoff: 8000, q: 0.7 } },
    { name: "Muffled", params: { cutoff: 2500, q: 0.7 } },
    { name: "Telephone", params: { cutoff: 3400, q: 1 } },
    { name: "Resonant Sweep", params: { cutoff: 4000, q: 6 } },
  ],
  highpass: [
    { name: "Rumble Cut", params: { cutoff: 80, q: 0.7 } },
    { name: "Vocal Cleanup", params: { cutoff: 120, q: 0.7 } },
    { name: "Tight", params: { cutoff: 200, q: 0.9 } },
    { name: "Thin / Radio", params: { cutoff: 500, q: 0.7 } },
  ],
  saturator: [
    { name: "Tape", params: { drive: 0.25, mix: 1, output: 0 } },
    { name: "Warm Drive", params: { drive: 0.45, mix: 0.9, output: -1 } },
    { name: "Crunch", params: { drive: 0.7, mix: 1, output: -3 } },
    { name: "Fuzz", params: { drive: 0.95, mix: 1, output: -5 } },
  ],
  chorus: [
    { name: "Subtle", params: { rate: 0.8, depth: 3, mix: 0.25 } },
    { name: "Lush", params: { rate: 1.2, depth: 7, mix: 0.45 } },
    { name: "Wide 80s", params: { rate: 0.5, depth: 10, mix: 0.5 } },
    { name: "Vibrato", params: { rate: 5, depth: 6, mix: 0.8 } },
  ],
  limiter: [
    { name: "Transparent", params: { threshold: -1, release: 0.1, output: 0 } },
    { name: "Loud Master", params: { threshold: -3, release: 0.05, output: 3 } },
    { name: "Brickwall", params: { threshold: -0.3, release: 0.03, output: 1 } },
    { name: "Gentle", params: { threshold: -6, release: 0.2, output: 2 } },
  ],
  width: [
    { name: "Mono", params: { width: 0 } },
    { name: "Natural", params: { width: 1 } },
    { name: "Wide", params: { width: 1.5 } },
    { name: "Extra Wide", params: { width: 1.9 } },
  ],
  denoise: [
    { name: "Light", params: { strength: 0.4 } },
    { name: "Medium", params: { strength: 0.7 } },
    { name: "Heavy", params: { strength: 0.95 } },
  ],
  deverb: [
    { name: "Close Mic Touch-Up", params: { decay: 0.4, damping: 0.5, reverb: -10, strength: 0.35, mix: 0.9 } },
    { name: "Light Room", params: { decay: 0.8, damping: 0.4, reverb: -6, strength: 0.4, mix: 0.8 } },
    { name: "Medium Room", params: { decay: 1.2, damping: 0.5, reverb: -3, strength: 0.5, mix: 0.85 } },
    { name: "Video-Call Rescue", params: { decay: 0.8, damping: 0.5, reverb: 2, strength: 0.65, mix: 0.85 } },
    { name: "Big Room / Hall", params: { decay: 2.5, damping: 0.6, reverb: 3, strength: 0.7, mix: 0.9 } },
    { name: "Aggressive", params: { decay: 1.5, damping: 0.7, reverb: 2, strength: 0.95, mix: 0.95 } },
  ],};
